import React, { useEffect, useMemo } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import {
  ShieldCheck,
  FileText,
  Lock,
  Cookie,
  CreditCard,
  AlertTriangle,
  FileCode,
  LifeBuoy,
  Scale,
  CheckCircle2,
  ChevronRight,
  Mail,
  Building2,
  Truck,
} from 'lucide-react';
import { BUSINESS_DETAILS, DATA_REGION_STATEMENT, REFUND_TERMS } from '@/src/modules/legal/businessDetails';

export interface PolicyDefinition {
  slug: string;
  title: string;
  category: 'legal' | 'privacy' | 'commerce' | 'operations';
  icon: React.ElementType;
  description: string;
  lastUpdated: string;
  content: {
    summary: string;
    sections: Array<{
      title: string;
      paragraphs: string[];
      bullets?: string[];
    }>;
  };
}

export const POLICY_CATALOG: Record<string, PolicyDefinition> = {
  'terms': {
    slug: 'terms',
    title: 'Terms & Conditions',
    category: 'legal',
    icon: Scale,
    description: 'General terms of service for the InviteCloud platform, pricing, payments and services.',
    lastUpdated: 'October 31, 2026',
    content: {
      summary: `These Terms & Conditions govern your access to and use of the InviteCloud platform (${BUSINESS_DETAILS.website}), its digital invitation design tools and services, operated by ${BUSINESS_DETAILS.legalName} under Indian jurisdiction.`,
      sections: [
        {
          title: '1. Acceptance of Terms & Electronic Record Audit Trail',
          paragraphs: [
            'By accessing, registering, or using InviteCloud, you acknowledge that you have read, understood, and agreed to these Terms, our Privacy Policy, Acceptable Use Policy, Cookie Policy, Cancellation & Refund Policy, Shipping & Delivery Policy, and any additional policies published on our Platform.',
            'In accordance with Section 10A of the Information Technology Act, 2000 (validity of contracts formed through electronic means), your affirmative acceptance via the Terms & User Agreement popup generates a timestamped electronic audit record (accepted version, acceptance timestamp, acceptance method) tied to your account. This electronic record constitutes a legally binding agreement between you and InviteCloud.',
          ],
        },
        {
          title: '2. Operating Entity & Jurisdiction',
          paragraphs: [
            `InviteCloud services are owned and operated by ${BUSINESS_DETAILS.legalName}, ${BUSINESS_DETAILS.registeredAddress}, India.`,
            'These Terms shall be governed by and construed in accordance with the laws of India. Any legal suit, action, or proceeding arising out of these Terms or the Platform shall be subject to the exclusive jurisdiction of the competent courts in India.',
          ],
        },
        {
          title: '3. User Accounts & Responsibilities',
          paragraphs: [
            'To access certain features of the Platform, you must register for an account using Google, Facebook, or Microsoft social authentication.',
            'You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.',
          ],
          bullets: [
            'You must provide accurate and complete account information.',
            'You must notify InviteCloud immediately of any unauthorized use of your account.',
            'Account sharing or selling account credentials to third parties is strictly prohibited.',
          ],
        },
        {
          title: '4. Pricing & Payments',
          paragraphs: [
            'InviteCloud does not sell subscriptions, monthly plans or trials, and nothing is charged automatically. All prices are shown in Indian Rupees (INR) on our Pricing page and at checkout before you pay.',
            'Hosts pay once per invitation when they publish it. Each design has a fixed price of ₹800, ₹1,000, ₹1,300, ₹1,700, ₹2,000 or ₹2,500, shown on the design before payment.',
            'Partners (planners, studios, venues and similar businesses) may buy fixed packs of prepaid credits: Starter (pay ₹5,000, receive 7,000 credits) or Pro (pay ₹20,000, receive 32,000 credits). 1 credit = ₹1 of design price. Prepaid credits are governed by our Prepaid Credits Policy.',
            'Payments are processed by our payment gateway partner, Razorpay. We may change prices for future purchases; we will give at least 30 days\' notice of any price increase, and a change never affects a purchase you have already paid for.',
          ],
        },
        {
          title: '5. Delivery, Cancellation & Refunds',
          paragraphs: [
            'Everything InviteCloud sells is a digital service delivered online; nothing is shipped physically. Delivery is described in our Shipping & Delivery Policy.',
            'Cancellations and refunds, including duplicate or failed payments, are handled under our Cancellation & Refund Policy.',
          ],
        },
        {
          title: '6. Intellectual Property & User Content License',
          paragraphs: [
            'InviteCloud retains all rights, title, and interest in and to the Platform, including design templates, video and animation tools, software, branding, and proprietary UI components.',
            'You retain ownership of all photos, text, and personal media uploaded to your invitations. By uploading content, you grant InviteCloud a non-exclusive, worldwide, royalty-free license to host, display, and process your content solely for operating your invitation service.',
          ],
        },
        {
          title: '7. Governing Contact',
          paragraphs: [
            `For any legal notices or terms inquiries, contact us at ${BUSINESS_DETAILS.grievanceEmail} or ${BUSINESS_DETAILS.supportEmail}. Full contact details are on our Contact Us page.`,
          ],
        },
      ],
    },
  },
  'privacy-policy': {
    slug: 'privacy-policy',
    title: 'Privacy Policy',
    category: 'privacy',
    icon: Lock,
    description: 'Data protection standards, DPDP Act 2023 compliance, guest privacy, and zero PII telemetry.',
    lastUpdated: 'October 31, 2026',
    content: {
      summary: 'InviteCloud is committed to protecting your personal data in accordance with the Digital Personal Data Protection Act, 2023 (DPDP Act) and global privacy standards.',
      sections: [
        {
          title: '1. Data We Collect',
          paragraphs: [
            'We collect minimal personal data necessary to provide our digital invitation and RSVP service.',
          ],
          bullets: [
            'Account Information: Name and email address provided during social authentication.',
            'Invitation Details: Event dates, venue locations, couple names, and uploaded event photos.',
            'Guest Responses: Guest names, RSVP attendance status, dietary preferences, and blessings messages.',
            'Payment Records: When you pay, your card, UPI or bank details are entered on Razorpay\'s secure checkout and are not stored by InviteCloud. We keep the payment reference, amount, date and status so we can issue invoices and process refunds.',
            'Aggregated Telemetry: Pageviews and template interactions via GA4 without capturing PII.',
          ],
        },
        {
          title: '2. Your Photos and Media',
          paragraphs: [
            'We store the photos, videos and other media you upload only to build and show your invitation. We do not sell your media or use it for advertising.',
            'AI features are optional. Only when you choose to use an AI feature, such as generating a template, AI images or an AI video from your photos, do we send the photos and details that feature needs to our third-party AI providers to process. If you do not use AI features, your photos are not sent to any AI provider.',
          ],
          bullets: [
            'How long we keep them: we keep your photos and media for as long as your draft or invitation needs them, including after your event so you and your guests can revisit it, and we delete them when they are no longer needed.',
            `Deletion on request: you can ask us to delete your photos or invitation at any time by writing to ${BUSINESS_DETAILS.supportEmail}. We will delete them unless the law requires us to keep certain records.`,
          ],
        },
        {
          title: '3. DPDP Act Compliance & Data Rights',
          paragraphs: [
            'Under the DPDP Act 2023, Indian users retain rights to access, correct, update, and request erasure of their personal data.',
            `To exercise your privacy rights or raise a data-protection grievance, contact our designated Grievance Officer, ${BUSINESS_DETAILS.grievanceOfficerName} (${BUSINESS_DETAILS.grievanceOfficerDesignation}), at ${BUSINESS_DETAILS.grievanceEmail}.`,
          ],
        },
        {
          title: '4. Data Security & Storage',
          paragraphs: [
            'All data sent between your device and InviteCloud is encrypted in transit using TLS.',
            `Our databases and application servers are hosted securely in ${DATA_REGION_STATEMENT}, with automated backups, and media files are delivered through a content delivery network.`,
          ],
        },
        {
          title: '5. Service Providers',
          paragraphs: [
            'We share only the data needed to run the service with providers that act on our behalf, such as our payment gateway (Razorpay), hosting and storage providers, email delivery, analytics, and AI providers when you use an AI feature. We do not sell your personal data.',
          ],
        },
      ],
    },
  },
  'acceptable-use-policy': {
    slug: 'acceptable-use-policy',
    title: 'Acceptable Use Policy',
    category: 'legal',
    icon: ShieldCheck,
    description: 'Prohibited conduct, automated AI safety checks, and third-party AI provider disclosures.',
    lastUpdated: 'July 26, 2026',
    content: {
      summary: 'This policy defines acceptable platform usage and content standards to ensure InviteCloud remains a safe, respectful environment for all event hosts and guests.',
      sections: [
        {
          title: '1. Prohibited Content & Activity',
          paragraphs: [
            'Users may not create invitations containing unlawful, abusive, defamatory, hateful, or explicit material.',
          ],
          bullets: [
            'Harassment, hate speech, or targeted discrimination against any group or individual.',
            'Unauthorized use of copyrighted artwork, audio tracks, or trademarked logos.',
            'Automated spam invitations, phishing links, or malware distribution.',
          ],
        },
        {
          title: '2. Content Review & AI Features',
          paragraphs: [
            'We may review uploaded invitation media and text to enforce this Policy, and we may remove content that breaks it.',
            'Optional AI features on InviteCloud use trusted third-party AI providers, which receive only the content needed for the feature you choose to use. See our Privacy Policy for details.',
          ],
        },
      ],
    },
  },
  'cancellation-refund-policy': {
    slug: 'cancellation-refund-policy',
    title: 'Cancellation & Refund Policy',
    category: 'commerce',
    icon: CreditCard,
    description: 'Cancellations, refund conditions and timelines for invitations and partner credit packs.',
    lastUpdated: 'October 31, 2026',
    content: {
      summary: `InviteCloud sells one-time digital purchases: a published invitation for hosts, and packs of prepaid credits for partners. There are no subscriptions to cancel. This policy explains when you can cancel, when you get a refund, where the money goes and how long it takes. Approved refunds to your bank, card or UPI usually reach you within ${REFUND_TERMS.bankCreditWorkingDays} after we start them.`,
      sections: [
        {
          title: '1. Cancelling Before You Pay',
          paragraphs: [
            'You can leave checkout at any time before payment and you will not be charged. An unpaid checkout simply expires.',
          ],
        },
        {
          title: '2. Hosts: Paying for an Invitation',
          paragraphs: [
            'Your invitation is delivered online as soon as payment succeeds and it is published. Because the digital service is delivered immediately, a successful purchase is not normally refundable just because you changed your mind.',
            'You are entitled to a full refund to your original payment method if:',
          ],
          bullets: [
            'there is a fault on our side with your invitation (it will not load, an image is not placed correctly, or publishing or rendering fails) that our service has detected or our team has confirmed after you report it, and we cannot fix or re-deliver it. Once the fault is detected or confirmed you have 24 hours to request the refund; we tell you by email and in-app when that window opens;',
            'you were charged twice for the same invitation (duplicate payment). Duplicates are refunded to your original payment method automatically, and you keep the invitation you paid for;',
            'the payment failed or was captured by mistake (wrong amount or wrong capture); or',
            'the transaction was not authorised by you.',
          ],
        },
        {
          title: '3. Partners: Prepaid Credit Packs',
          paragraphs: [
            `You can ask for a refund of a Starter or Pro pack within ${REFUND_TERMS.packRefundWindow} of purchase. We refund the amount you paid minus the credits already used, counted at ₹1 per credit. Example: you paid ₹5,000 for a Starter pack and used 1,999 credits, so we refund ₹3,001 to your original payment method and the pack's remaining credits are removed.`,
            'After that window, unused credits are not refunded as cash (they never expire, so you can keep using them), except where the law requires a refund or in the cases below.',
            'If you are charged twice for the same pack, the duplicate is handled automatically: the extra amount is added to your account as prepaid credits at the pack\'s rate, and you can ask for it to be refunded to your bank instead from Settings → Refunds within 7 days, before you use any of those credits. The pack you paid for is unaffected. A failed, wrongly captured or unauthorised payment is refunded to the original payment method.',
          ],
        },
        {
          title: '4. Changing Your Template',
          paragraphs: [
            'You may change the template of an invitation as often as you like before you pay for it. For partners, this means before any credits are used for that invitation.',
            'Once payment has been made for an invitation, or credits have been used for it, the invitation is tied to the template that was paid for, and that template cannot be changed. If you want a different template, you must make a new purchase for that template at its listed price.',
            'Cancelling, deleting or unpublishing a paid or published invitation, or deciding that you want a different template, does not entitle you to a refund or to the return of any credits used. This applies to hosts and partners alike. It does not affect your right to a refund in the cases listed in Sections 2 and 3, such as a fault on our side, a duplicate payment, or a failed, wrongly captured or unauthorised payment.',
          ],
        },
        {
          title: '5. How to Ask for a Refund',
          paragraphs: [
            `Email ${BUSINESS_DETAILS.supportEmail} with your registered email address, the order number (ORD-...) or Razorpay payment reference (pay_...) and the reason. Duplicate payments are handled automatically, so you only need to tell us if that did not happen. Please report a failed or unauthorised payment, or a duplicate that was not handled, within ${REFUND_TERMS.paymentIssueReportWindow}; late reports are still reviewed.`,
            'Every purchase carries a unique InviteCloud order number (ORD-...) and a payment gateway reference (pay_...), which we use to trace and resolve your request.',
          ],
        },
        {
          title: '6. Refund Timelines',
          paragraphs: [
            'Here is how long each step takes:',
          ],
          bullets: [
            'We acknowledge your request within 48 hours.',
            'We resolve it within 5 business days of receiving everything we need.',
            'If approved, we start the refund through Razorpay within 2 working days.',
            `The money reaches your original payment method (bank account, card or UPI) within ${REFUND_TERMS.bankCreditWorkingDays} after we start it, depending on your bank.`,
            'If a payment fails but money is debited, it is normally reversed automatically by the bank or gateway within about 5 working days, as per RBI timelines, without you needing to ask.',
          ],
        },
        {
          title: '7. Chargebacks',
          paragraphs: [
            'If you raise a chargeback with your bank, we may pause the related invitation or prepaid credits while the dispute is reviewed. Please contact us first; we can usually resolve payment problems faster directly.',
          ],
        },
      ],
    },
  },
  'shipping-delivery-policy': {
    slug: 'shipping-delivery-policy',
    title: 'Shipping & Delivery Policy',
    category: 'commerce',
    icon: Truck,
    description: 'How digital invitations and prepaid credits are delivered. No physical shipping.',
    lastUpdated: 'October 31, 2026',
    content: {
      summary: 'InviteCloud sells digital services only. Nothing is shipped physically and there are no shipping charges. Everything is delivered online to your InviteCloud account.',
      sections: [
        {
          title: '1. What We Deliver',
          paragraphs: [
            'Hosts receive a published digital invitation with a shareable link, available in their InviteCloud dashboard.',
            'Partners receive prepaid credits added to their InviteCloud account, which they use to publish invitations for their clients.',
          ],
        },
        {
          title: '2. Delivery Time',
          paragraphs: [
            'Delivery is normally immediate. Your invitation goes live, or your credits appear in your account, as soon as the payment is confirmed. In rare cases a payment confirmation can take a few minutes.',
            `If something goes wrong with delivery, such as a publish or render failure, we retry automatically once and alert our team. If the fault is still there, we tell you by email and in-app, try to fix or re-deliver it, and you then have 24 hours from when we detect or confirm the fault to request a refund under our Cancellation & Refund Policy. If you notice a problem we have not flagged, email ${BUSINESS_DETAILS.supportEmail} with your payment reference and the window opens once our team confirms it.`,
          ],
        },
        {
          title: '3. Delivery Location & Charges',
          paragraphs: [
            'Delivery is online, to the email address and account you use on InviteCloud. There are no shipping or delivery charges.',
          ],
        },
      ],
    },
  },
  'contact-us': {
    slug: 'contact-us',
    title: 'Contact Us',
    category: 'operations',
    icon: Mail,
    description: 'How to reach InviteCloud for support, billing, refunds and grievances.',
    lastUpdated: 'October 31, 2026',
    content: {
      summary: `InviteCloud is operated by ${BUSINESS_DETAILS.legalName}. Reach us by email for help with invitations, payments, refunds or any complaint.`,
      sections: [
        {
          title: '1. Business Details',
          paragraphs: [],
          bullets: [
            `Legal name: ${BUSINESS_DETAILS.legalName}`,
            `Trade name: ${BUSINESS_DETAILS.tradeName}`,
            `Operating address: ${BUSINESS_DETAILS.operatingAddress}`,
            `Registered office: ${BUSINESS_DETAILS.registeredAddress}`,
            ...(BUSINESS_DETAILS.supportPhone ? [`Phone: ${BUSINESS_DETAILS.supportPhone}`] : []),
            `Email: ${BUSINESS_DETAILS.supportEmail}`,
            `Website: ${BUSINESS_DETAILS.website}`,
          ],
        },
        {
          title: '2. Support, Billing & Refunds',
          paragraphs: [
            `Email ${BUSINESS_DETAILS.supportEmail} for help with your invitations, payments or refunds. Signed-in users can also raise a ticket from the in-app help desk.`,
            `Support hours: ${BUSINESS_DETAILS.businessHours}.`,
          ],
        },
        {
          title: '3. Grievance Officer',
          paragraphs: [
            `For complaints under the Information Technology Act, 2000, the Digital Personal Data Protection Act, 2023 or the Consumer Protection (E-Commerce) Rules, 2020, write to our Grievance Officer, ${BUSINESS_DETAILS.grievanceOfficerName} (${BUSINESS_DETAILS.grievanceOfficerDesignation}), at ${BUSINESS_DETAILS.grievanceEmail}. We acknowledge complaints within 48 hours and resolve them within 5 business days.`,
          ],
        },
      ],
    },
  },
  'cookies-policy': {
    slug: 'cookies-policy',
    title: 'Cookies Policy',
    category: 'privacy',
    icon: Cookie,
    description: 'Cookie usage, GA4 telemetry disclosure, and zero PII tracking declaration.',
    lastUpdated: 'July 26, 2026',
    content: {
      summary: 'InviteCloud uses essential cookies for user authentication and session security, alongside aggregated GA4 analytics with zero PII captured.',
      sections: [
        {
          title: '1. Essential vs Analytics Cookies',
          paragraphs: [
            'Essential cookies are required for user login and session persistence. Analytics cookies measure overall platform engagement and template popularity.',
          ],
        },
      ],
    },
  },
  'copyrights-policy': {
    slug: 'copyrights-policy',
    title: 'Copyrights Policy',
    category: 'legal',
    icon: FileCode,
    description: 'IP ownership, user license terms, trademark application status, and DMCA notice process.',
    lastUpdated: 'July 26, 2026',
    content: {
      summary: 'Protects intellectual property rights of creators and InviteCloud while detailing copyright notice submission procedures.',
      sections: [
        {
          title: '1. Trademark & Content Protection',
          paragraphs: [
            'InviteCloud trademark registration is in progress under Indian intellectual property laws. Users retain full rights to their original uploaded media.',
          ],
        },
      ],
    },
  },
  'content-moderation-policy': {
    slug: 'content-moderation-policy',
    title: 'Content Moderation Policy',
    category: 'operations',
    icon: AlertTriangle,
    description: 'Moderation workflow, grievance officer details, and content appeal procedures.',
    lastUpdated: 'July 26, 2026',
    content: {
      summary: 'Explains how content complaints are investigated, actioned, and appealed under Indian Intermediary Guidelines.',
      sections: [
        {
          title: '1. Grievance Redressal Mechanism',
          paragraphs: [
            `Grievance complaints may be submitted to our appointed Grievance Officer, ${BUSINESS_DETAILS.grievanceOfficerName} (${BUSINESS_DETAILS.grievanceOfficerDesignation}), at ${BUSINESS_DETAILS.grievanceEmail} or ${BUSINESS_DETAILS.supportEmail}.`,
          ],
        },
      ],
    },
  },
  'disclaimer': {
    slug: 'disclaimer',
    title: 'Disclaimer',
    category: 'legal',
    icon: FileText,
    description: 'Limitation of liability, service availability disclaimers, and consumer protection rights.',
    lastUpdated: 'July 26, 2026',
    content: {
      summary: 'Outlines service availability terms, AI generation disclaimers, and statutory consumer rights under Indian law.',
      sections: [
        {
          title: '1. Service & AI Generation Disclaimer',
          paragraphs: [
            'Platform services and AI generation features are provided on an "as is" and "as available" basis.',
          ],
        },
      ],
    },
  },
  'security-policy': {
    slug: 'security-policy',
    title: 'Security Policy',
    category: 'operations',
    icon: ShieldCheck,
    description: 'How we protect your data, and how to report a security issue.',
    lastUpdated: 'October 31, 2026',
    content: {
      summary: 'Describes how we keep InviteCloud and your data secure, and how to report a vulnerability.',
      sections: [
        {
          title: '1. How We Secure the Platform',
          paragraphs: [
            'All data sent between your device and InviteCloud is encrypted in transit using TLS (HTTPS).',
            'Payments are handled on Razorpay\'s secure checkout. InviteCloud does not see or store your card, UPI or bank details.',
          ],
        },
        {
          title: '2. Reporting a Security Issue',
          paragraphs: [
            `If you believe you have found a security problem on InviteCloud, please email ${BUSINESS_DETAILS.supportEmail} with the details. Please do not access, change or share other people\'s data while testing.`,
          ],
        },
      ],
    },
  },
  'subscription-policy': {
    slug: 'subscription-policy',
    title: 'Prepaid Credits Policy',
    category: 'commerce',
    icon: CreditCard,
    description: 'No subscriptions. How partner prepaid credit packs work, and their limits.',
    lastUpdated: 'October 31, 2026',
    content: {
      summary: 'InviteCloud has no subscriptions, monthly plans or automatic renewals. Hosts pay once per invitation. Partners can buy fixed packs of prepaid credits, which are used only to publish invitations on InviteCloud.',
      sections: [
        {
          title: '1. No Subscriptions',
          paragraphs: [
            'There is nothing to subscribe to and nothing to cancel. We never charge you automatically or on a schedule.',
          ],
        },
        {
          title: '2. Partner Credit Packs',
          paragraphs: [
            '1 credit = ₹1 of design price. A design priced at ₹1,300 uses 1,300 credits when you publish it.',
          ],
          bullets: [
            'Starter pack: pay ₹5,000, receive 7,000 credits.',
            'Pro pack: pay ₹20,000, receive 32,000 credits.',
            'The extra credits in each pack are the only discount; there are no promotional bonuses or coupons on packs.',
            'Credits never expire.',
            'Each invitation published with credits includes your partner branding and analytics, which it keeps after publishing.',
          ],
        },
        {
          title: '3. Nature of Prepaid Credits',
          paragraphs: [
            'Prepaid credits are a prepaid balance for InviteCloud services only. They are not money, a bank deposit or a payment instrument.',
          ],
          bullets: [
            'Prepaid credits are non-transferable: they cannot be moved, gifted or sold to another account or person.',
            'Prepaid credits cannot be withdrawn or redeemed as cash, except for the refunds described in our Cancellation & Refund Policy.',
            'Prepaid credits can be used only for invitations on InviteCloud.',
          ],
        },
        {
          title: '4. Refunds & Price Changes',
          paragraphs: [
            'Refunds of packs, including duplicate payments and partly used packs, follow our Cancellation & Refund Policy.',
            'We will give at least 30 days\' notice before increasing the price of any pack or design. Credits you already hold are not reduced by a price change.',
          ],
        },
        {
          title: '5. Invoices & GST',
          paragraphs: [
            'We issue an invoice or receipt for every purchase. The price shown before you pay is the final amount you pay; there are no hidden charges.',
          ],
        },
      ],
    },
  },
  'support-policy': {
    slug: 'support-policy',
    title: 'Support Policy',
    category: 'operations',
    icon: LifeBuoy,
    description: 'In-app help desk (/dashboard/help), support SLA targets, and AI generation assistance.',
    lastUpdated: 'July 26, 2026',
    content: {
      summary: 'Details support channels including the in-app ticketing dashboard (/dashboard/help) and AI video support scope.',
      sections: [
        {
          title: '1. Support Channels & Ticketing',
          paragraphs: [
            'Users can submit help tickets directly via /dashboard/help or email support@invitecloud.in and founder@invitecloud.in.',
          ],
        },
      ],
    },
  },
};

POLICY_CATALOG['prepaid-credits-policy'] = {
  ...POLICY_CATALOG['subscription-policy'],
  slug: 'prepaid-credits-policy',
};

export function PolicyPage({ defaultSlug }: { defaultSlug?: string }) {
  const { slug: routeSlug } = useParams<{ slug?: string }>();
  const location = useLocation();

  // Determine target policy slug
  const activeSlug = useMemo(() => {
    if (defaultSlug) return defaultSlug;
    if (routeSlug) return routeSlug;
    // Fallback based on pathname if direct route hit like /privacy-policy
    const path = location.pathname.replace(/^\//, '');
    if (path === 'privacy' || path === 'privacy-policy') return 'privacy-policy';
    if (path === 'terms-and-conditions' || path === 'terms') return 'terms';
    if (path === 'refund-policy') return 'cancellation-refund-policy';
    if (path === 'shipping-policy' || path === 'delivery-policy') return 'shipping-delivery-policy';
    if (path === 'contact') return 'contact-us';
    if (path === 'subscription-policy' || path === 'prepaid-credits-policy') return 'prepaid-credits-policy';
    return POLICY_CATALOG[path] ? path : 'terms';
  }, [defaultSlug, routeSlug, location.pathname]);

  const policy = POLICY_CATALOG[activeSlug] || POLICY_CATALOG['terms'];

  // Update browser tab title for SEO and direct link sharing.
  useEffect(() => {
    const prev = document.title;
    document.title = `${policy.title} | InviteCloud`;
    return () => { document.title = prev; };
  }, [policy.title]);

  return (
    <div className="min-h-screen bg-surface-lowest text-on-surface">
      {/* Policy Header Banner */}
      <section className="relative overflow-hidden border-b border-outline-variant/20 bg-surface/50 py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3.5 py-1 text-xs font-bold text-primary">
              <policy.icon className="h-3.5 w-3.5" />
              InviteCloud Official Policy
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-on-surface">
              {policy.title}
            </h1>
            <p className="text-sm text-on-surface-variant font-medium">
              {policy.description}
            </p>
          </div>

          <div className="flex flex-col items-start md:items-end gap-1.5 text-xs text-on-surface-variant bg-white/80 p-4 rounded-2xl border border-outline-variant/30 shadow-sm">
            <div className="flex items-center gap-1.5 font-bold text-on-surface">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              Effective Date: {policy.lastUpdated}
            </div>
            <span>Entity: {BUSINESS_DETAILS.legalName}</span>
            <span>Jurisdiction: India</span>
          </div>
        </div>
      </section>

      {/* Main Layout Grid */}
      <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10">
        {/* Sticky Left Navigation Sidebar */}
        <aside className="space-y-6">
          <div className="sticky top-24 rounded-2xl border border-outline-variant/30 bg-white p-5 shadow-sm space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant px-2">
              Platform Policy Index
            </h3>
            <nav className="space-y-1">
              {Object.values(POLICY_CATALOG).map((item) => {
                const isActive = item.slug === activeSlug;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.slug}
                    to={`/${item.slug}`}
                    className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-xs font-bold transition-all ${
                      isActive
                        ? 'bg-primary text-on-primary shadow-sm'
                        : 'text-on-surface-variant hover:bg-surface-lowest hover:text-on-surface'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{item.title}</span>
                    </div>
                    <ChevronRight className={`h-3.5 w-3.5 shrink-0 opacity-60 ${isActive ? 'text-on-primary' : ''}`} />
                  </Link>
                );
              })}
            </nav>

            <div className="pt-4 border-t border-outline-variant/20 space-y-3">
              <h4 className="text-xs font-bold text-on-surface px-2">Grievance & Support</h4>
              <a
                href={`mailto:${BUSINESS_DETAILS.grievanceEmail}`}
                className="flex items-center gap-2 text-xs font-medium text-on-surface-variant hover:text-primary transition-colors px-2"
              >
                <Mail className="h-3.5 w-3.5 text-primary" />
                founder@invitecloud.in
              </a>
              <a
                href="mailto:support@invitecloud.in"
                className="flex items-center gap-2 text-xs font-medium text-on-surface-variant hover:text-primary transition-colors px-2"
              >
                <Mail className="h-3.5 w-3.5 text-primary" />
                support@invitecloud.in
              </a>
            </div>
          </div>
        </aside>

        {/* Policy Document Content Viewer */}
        <main className="space-y-8">
          {/* Executive Summary Box */}
          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 space-y-2">
            <h3 className="text-sm font-bold text-primary flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              Executive Summary
            </h3>
            <p className="text-xs font-medium text-on-surface leading-relaxed">
              {policy.content.summary}
            </p>
          </div>

          {/* Policy Sections */}
          <div className="rounded-3xl border border-outline-variant/30 bg-white p-6 sm:p-10 shadow-sm space-y-8">
            {policy.content.sections.map((section, idx) => (
              <div key={idx} className="space-y-4 border-b border-outline-variant/20 last:border-0 pb-8 last:pb-0">
                <h3 className="text-xl font-bold text-on-surface">
                  {section.title}
                </h3>
                {section.paragraphs.map((p, pIdx) => (
                  <p key={pIdx} className="text-sm text-on-surface-variant leading-relaxed">
                    {p}
                  </p>
                ))}
                {section.bullets && (
                  <ul className="space-y-2 pt-1">
                    {section.bullets.map((b, bIdx) => (
                      <li key={bIdx} className="flex items-start gap-2.5 text-xs text-on-surface-variant font-medium">
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          {/* Footer Contact Card */}
          <div className="rounded-2xl border border-outline-variant/30 bg-white p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Building2 className="h-6 w-6 text-primary" />
              <div>
                <h4 className="text-sm font-bold text-on-surface">{BUSINESS_DETAILS.legalName}</h4>
                <p className="text-xs text-on-surface-variant">Operating entity under Indian jurisdiction (invitecloud.in)</p>
              </div>
            </div>
            <a
              href={`mailto:${BUSINESS_DETAILS.grievanceEmail}`}
              className="px-4 py-2 rounded-xl bg-surface border border-outline-variant text-xs font-bold text-on-surface hover:border-primary transition-all"
            >
              Contact Grievance Officer
            </a>
          </div>
        </main>
      </div>
    </div>
  );
}

export default PolicyPage;
